import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Drawer } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro } from '../api';
import { relativo, type Idioma } from '../formato';
import { useSinal } from '../tempo-real';
import { textoDaNotificacao, useContador, useMarcarLidas, useNaoLidas, useOpcoes, useRecarregarNoSinal, type Notificacao, type Som } from './dados';
import { tocarSom } from './som';

/** Item da lista (sino e central): ponto de não lida com texto para o leitor, título, frase e quando. */
export function ItemNotificacao({ n, aoAbrir }: { n: Notificacao; aoAbrir: (n: Notificacao) => void }) {
  const { t, i18n } = useTranslation();
  const { titulo, corpo } = textoDaNotificacao(t, n, i18n.language as Idioma);
  return (
    <button type="button" data-notificacao={n.tipo} onClick={() => aoAbrir(n)} className="flex w-full min-w-0 items-start gap-2 rounded-lg px-2 py-2 text-left hover:bg-sup-3 focus-visible:outline-2 focus-visible:outline-foco">
      <span aria-hidden className={`mt-1.5 size-2 shrink-0 rounded-full ${n.lida_em ? 'bg-transparent' : 'bg-acento-forte'}`} />
      <span className="min-w-0 flex-1">
        {!n.lida_em && <span className="sr-only">{t('notificacoes.naoLida')}: </span>}
        <span className={`block text-sm break-words ${n.lida_em ? 'text-tinta-2' : 'font-semibold text-tinta-1'}`}>{titulo}</span>
        {corpo && (
          <TextoCortado linhas={2} className="text-xs text-tinta-2">
            {corpo}
          </TextoCortado>
        )}
        <span className="numero block text-xs text-tinta-3">{relativo(n.created_at)}</span>
      </span>
    </button>
  );
}

/** Abre o destino do aviso e marca como lido. */
export function useAbrirNotificacao(depois?: () => void) {
  const navegar = useNavigate();
  const marcar = useMarcarLidas();
  return (n: Notificacao) => {
    if (!n.lida_em) marcar.mutate({ ids: [n.id] });
    depois?.();
    if (n.link) navegar(n.link);
  };
}

/**
 * Fase 2 (W3): contador de não lidas, som e recarga pelo sinal. Fase 2 (W31, item 2): o sino saiu da barra superior; o vigia
 * (uma vez no Shell) segura o que ele fazia sem depender de onde o contador aparece: recarga no sinal de aviso, som (opcional,
 * nunca com o modo discreto) e o anúncio polido do contador para o leitor de tela.
 */
export function VigiaNotificacoes() {
  const { t } = useTranslation();
  useRecarregarNoSinal();
  const contador = useContador();
  const opcoes = useOpcoes();
  useSinal('notificacao', (d) => {
    if (typeof d.tipo === 'string') tocarSom((d.som as Som) ?? 'padrao', !!opcoes.data?.som);
  });
  const n = contador.data?.nao_lidas ?? 0;
  return (
    <span className="sr-only" aria-live="polite" data-livre-do-trap>
      {contador.isSuccess ? t('notificacoes.sinoContagem', { count: n }) : ''}
    </span>
  );
}

/** Selo de não lidas (mesmo desenho do selo do Comunicação): some com zero; `compacto` no trilho, `noBotao` no botão do menu do celular. */
export function SeloNotificacoes({ compacto, noBotao }: { compacto?: boolean; noBotao?: boolean }) {
  const { t } = useTranslation();
  const n = useContador().data?.nao_lidas ?? 0;
  if (!n) return null;
  return (
    <span
      data-selo-notificacoes
      className={`numero flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-acento px-1.5 text-xs font-semibold text-acento-tinta ${noBotao ? 'pointer-events-none absolute -top-1 -right-1' : compacto ? 'absolute top-0 right-1' : ''}`}
    >
      <span aria-hidden>{n > 99 ? '99+' : n}</span>
      <span className="sr-only">{t('notificacoes.naoLidasSelo', { count: n })}</span>
    </span>
  );
}

/** Caminho da central de notificações (Minha conta), destino do item do menu lateral. */
export const CAMINHO_NOTIFICACOES = '/conta/notificacoes';

/**
 * Fase 2 (W31, item 2): item "Notificações" do menu lateral (aberto, recolhido e gaveta do celular), com o selo de não lidas.
 * Mesmo desenho, foco e marcação de ativo (`aria-current` e barra de 3px) dos itens dos menus.
 */
export function ItemMenuNotificacoes({ compacto, aoNavegar }: { compacto?: boolean; aoNavegar?: () => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const ativo = pathname === CAMINHO_NOTIFICACOES || pathname.startsWith(`${CAMINHO_NOTIFICACOES}/`);
  const rotulo = t('notificacoes.sino');
  return (
    <Link
      to={CAMINHO_NOTIFICACOES}
      onClick={aoNavegar}
      aria-current={ativo ? 'page' : undefined}
      data-item-menu="notificacoes"
      title={compacto ? rotulo : undefined}
      className={`relative flex items-center gap-3 rounded-lg border-l-3 py-2 text-sm font-medium transition-colors ${compacto ? 'justify-center px-0' : 'px-3'} ${ativo ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-transparent text-tinta-2 hover:bg-sup-2 hover:text-tinta-1'}`}
    >
      <Bell className="size-4 shrink-0" aria-hidden />
      <span className={compacto ? 'sr-only' : 'min-w-0 flex-1'}>{rotulo}</span>
      <SeloNotificacoes compacto={compacto} />
    </Link>
  );
}

/**
 * CRM no novo desenho, lote 0 (complemento 7 do clone do RD): o pop-up do sino mostra só as não lidas. Marcar como lida
 * (a caixa ao lado) tira a notificação daqui; abrir também marca e leva ao destino. As lidas ficam só na central (Minha
 * conta > Notificações, aba Lidas). Sem novas, diz quantas lidas estão lá.
 */
export function PopupNotificacoes({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const toast = useToast();
  const lista = useNaoLidas(aberto);
  const contador = useContador();
  const marcar = useMarcarLidas();
  const abrir = useAbrirNotificacao(aoFechar);
  const itens = lista.data?.itens ?? [];
  const lidas = contador.data?.lidas ?? 0;
  const naoLidas = Math.max(contador.data?.nao_lidas ?? 0, itens.length);
  const erro = (e: unknown) => toast.erro(mensagemDeErro(e));
  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('notificacoes.sino')}
      rodape={
        <Botao
          variante="secundario"
          className="w-full justify-center"
          data-ver-todas-notificacoes=""
          onClick={() => {
            aoFechar();
            navegar(CAMINHO_NOTIFICACOES);
          }}
        >
          {t('notificacoes.verTodasNotificacoes')}
        </Botao>
      }
    >
      <div data-popup-notificacoes="" className="flex flex-col gap-3">
        {lista.isPending ? (
          <Carregando />
        ) : lista.isError ? (
          <Erro erro={lista.error} aoTentar={() => lista.refetch()} />
        ) : itens.length === 0 ? (
          <div data-popup-vazio="" className="flex flex-col items-center gap-2 px-2 py-10 text-center">
            <Bell className="size-6 text-tinta-3" aria-hidden />
            <p className="font-semibold text-tinta-1">{t('notificacoes.nenhumaNova')}</p>
            <p className="text-sm text-tinta-3">{lidas ? t('notificacoes.lidasNaCentral', { count: lidas }) : t('notificacoes.semNada')}</p>
          </div>
        ) : (
          <>
            <Botao
              variante="secundario"
              className="w-full justify-center"
              carregando={marcar.isPending && !!marcar.variables?.todas}
              onClick={() => marcar.mutate({ todas: true }, { onSuccess: () => toast.sucesso(t('notificacoes.todasLidas')), onError: erro })}
            >
              {t('notificacoes.marcarTodas')}
            </Botao>
            <p className="text-sm font-semibold text-tinta-1">{t('notificacoes.novas', { count: naoLidas })}</p>
            <ul className="flex flex-col gap-2">
              {itens.map((n) => (
                <li key={n.id} data-notificacao-popup={n.id} className="flex items-start gap-2 rounded-lg border border-borda p-2">
                  <input
                    type="checkbox"
                    className="mt-2.5 size-4 shrink-0 accent-acento-forte"
                    aria-label={t('notificacoes.marcarLida')}
                    title={t('notificacoes.marcarLida')}
                    disabled={marcar.isPending}
                    onChange={() => marcar.mutate({ ids: [n.id] }, { onError: erro })}
                  />
                  <div className="min-w-0 flex-1">
                    <ItemNotificacao n={n} aoAbrir={abrir} />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Drawer>
  );
}
